import { Database } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import NicerBlockquote from "../../common/NicerBlockquote"
import DataDeliveryIllustration from "../../home/DataDeliveryIllustration"
import ELTPipelineIllustration from "../../home/ELTPipelineIllustration"
import ProductPageHeader from "../ProductPageHeader"
import ProductPageTemplate from "../ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  ProductCTASection,
  StatsGrid,
  ThreeFeaturesSection,
} from "../shared"
import { SchemaViewer } from "./SchemaViewer"

const DatasharePage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Database}
          title="Datashare"
          subtitle="Direct access to the data layer behind every Token Terminal product, in your own BigQuery or Snowflake."
          ctaButtons={
            <>
              <LinkButton
                key="contact-sales"
                size="large"
                variant="solid"
                href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
                style={{ zIndex: 4 }}
              >
                Contact sales
              </LinkButton>
              <LinkButton
                key="explore-the-catalog"
                size="large"
                variant="ghost"
                href="https://tokenterminal.com/docs/catalog"
                style={{ zIndex: 4 }}
              >
                Explore the data catalog
              </LinkButton>
            </>
          }
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/home/hero-illustration-warehouse2.png`}
          whoWeServeText="Analysts, engineers, and data teams working with the underlying datasets directly."
          simpleWhoWeServeText="analysts, engineers, and data teams"
        />
      }
      productId="datashare"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip logos={productPageLogos.datashare} />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <SchemaViewer />
              </FeatureCard>
            ),
            title: (
              <>
                Go beyond
                <br />
                standardized metrics
              </>
            ),
            description:
              "Move from a protocol's revenue to the markets that generated it, from a DEX's volume to individual pools and trades, from market cap to holders and transfers.",
          },
          {
            component: (
              <FeatureCard className="h-[300px] flex items-center justify-center p-0">
                <div className="w-full h-full flex items-center justify-center">
                  <ELTPipelineIllustration />
                </div>
              </FeatureCard>
            ),
            title: "11,000+ decoded smart contracts",
            description:
              "Skip months of decoding work. Get human-readable events and function calls for the projects you need.",
          },
          {
            component: (
              <FeatureCard className="h-[300px] flex items-center justify-center p-0">
                <div className="w-full h-full flex items-center justify-center">
                  <DataDeliveryIllustration />
                </div>
              </FeatureCard>
            ),
            title: "Delivered to your data warehouse",
            description:
              "Datasets land in BigQuery or Snowflake, the environments your analysts already work in. Combine them with internal data and define your own methodologies.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="At CF Benchmarks we have found Token Terminal to be the best of breed of blockchain data providers. First and foremost their data is accurate and comprehensive, of imperative importance to a UK FCA regulated benchmark administrator like ourselves. On top of that the data interrogation tools and APIs across all their products are reliable, intuitive and performant. Hard to ask for more."
          author="Sui Chung"
          authorTitle="CEO at CF Benchmarks"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-CFBenchmarks.jpg`}
        />
      </div>

      {/* CTA Section */}
      <ProductCTASection
        tiers={[
          {
            id: "docs",
            title: "Explore the data catalog",
            features: [
              "Every served table documented",
              "Query examples for each dataset",
              "Column-level definitions and units",
              "Partition, cluster, and access guides",
            ],
            cta: (
              <LinkButton
                className="w-full mt-4"
                size="large"
                href="https://tokenterminal.com/docs/catalog"
                target="_blank"
              >
                View the data catalog
              </LinkButton>
            ),
          },
          {
            id: "enterprise",
            title: "Get Datashare access",
            badge: { label: "ENTERPRISE", color: "green" },
            features: [
              "Direct access in BigQuery and Snowflake",
              "Every layer, from raw blockchain data to standardized metrics",
              "Custom datasets built around your workflow",
              "Dedicated support",
            ],
            cta: (
              <LinkButton
                className="w-full mt-4"
                size="large"
                href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
                color="success"
              >
                Contact sales
              </LinkButton>
            ),
          },
        ]}
      />
    </ProductPageTemplate>
  )
}

export default DatasharePage
